import SEOHead from "@/components/SEOHead";
import Layout from "@/components/layout/Layout";
import Hero from "@/components/home/Hero";
import IntroBlurb from "@/components/home/IntroBlurb";
import ValueProposition from "@/components/home/ValueProposition";
import WhyPrivateMatters from "@/components/home/WhyPrivateMatters";
import VideoSection from "@/components/home/VideoSection";
import HowItWorks from "@/components/home/HowItWorks";
import FeaturesGrid from "@/components/home/FeaturesGrid";
import WatercraftRentalsSection from "@/components/landing/WatercraftRentalsSection";
import PhotoGallery from "@/components/home/PhotoGallery";
import Testimonials from "@/components/home/Testimonials";
import PricingPreview from "@/components/home/PricingPreview";
import CallToAction from "@/components/home/CallToAction";
import OtherProperties from "@/components/home/OtherProperties";
import OvernightSisterProperties from "@/components/landing/OvernightSisterProperties";
import JumpNav from "@/components/shared/JumpNav";
import BookCTASection from "@/components/shared/BookCTASection";
import CabanaPricingCards from "@/components/shared/CabanaPricingCards";
import CabanasOverviewSection from "@/components/shared/CabanasOverviewSection";
import ActivitiesIconGrid from "@/components/shared/ActivitiesIconGrid";
import HoursParkingFAQ from "@/components/shared/HoursParkingFAQ";

const homepageFaqs = [
  { q: "What are your hours?", a: "Open daily during the season, typically 10:30 AM – 6:30 PM for day cabanas. Reopening Spring 2026." },
  { q: "Where do we park?", a: "Each cabana reservation includes 2 parking spots on-site, just across the bridge from the island." },
  { q: "What should we bring?", a: "Swimsuits, towels, sunscreen, food and drinks for the BBQ. We provide the cabana, dock, grill, and amenities." },
  { q: "How many guests can a cabana hold?", a: "One cabana reserves entry for up to 8 guests, with a maximum of 15 guests per cabana." },
  { q: "Are pets allowed?", a: "Sorry, no pets are allowed on the island to keep it comfortable for all guests." },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": homepageFaqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

const jumpNavItems = [
  { id: "book", label: "Book" },
  { id: "pricing", label: "Pricing" },
  { id: "cabanas", label: "Cabanas" },
  { id: "activities", label: "Activities" },
  { id: "faq", label: "FAQ" },
];

const Index = () => {
  return (
    <>
      <SEOHead
        title="Son's Island: Private Daycation Near San Antonio"
        description="Skip the crowds. A private 3.5-acre island with cabanas, kayaks, paddle boards & glamping on Lake Placid — 45 minutes from San Antonio."
        keywords="fun things to do near San Antonio, family getaway near San Antonio, kayaking near San Antonio, paddle boarding near San Antonio, things to do with kids near San Antonio, day trips from San Antonio, private island Texas, glamping near San Antonio, cabana rentals near San Antonio, Son's Island, Lake Placid Seguin"
        canonical="https://sonsisland.com/"
        ogImage="https://sonsisland.com/og-logo.png"
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Son's Island",
            "alternateName": "Son's Island Getaways, LLC.",
            "url": "https://sonsisland.com",
            "logo": "https://sonsisland.com/og-logo.png",
            "description": "Son's Island is a private island family getaway near San Antonio offering cabana rentals, glamping, kayaking & paddle boarding in the Texas Hill Country.",
            "sameAs": [
              "https://www.facebook.com/sonsislandfun/",
              "https://www.instagram.com/sons.island/",
              "https://www.youtube.com/c/SonsIsland",
              "https://www.sonsriocibolo.com",
              "https://www.sonsguadalupe.com",
              "https://www.sonsriverranch.com",
              "https://www.sonsblueriver.com"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+18775777667",
              "contactType": "customer service",
              "email": "info@sonsgetaways.com",
              "areaServed": "US",
              "availableLanguage": ["English", "Spanish"]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Son's Island",
            "url": "https://sonsisland.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://sonsisland.com/blog?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "LodgingBusiness",
            "name": "Son's Island",
            "description": "Private island family getaway near San Antonio with cabana rentals, glamping & overnight stays.",
            "url": "https://sonsisland.com",
            "telephone": "+18775777667",
            "priceRange": "$$",
            "address": { "@type": "PostalAddress", "streetAddress": "110 Lee St", "addressLocality": "Seguin", "addressRegion": "TX", "postalCode": "78155", "addressCountry": "US" },
            "geo": { "@type": "GeoCoordinates", "latitude": 29.5205, "longitude": -97.9036 },
            "amenityFeature": [
              { "@type": "LocationFeatureSpecification", "name": "Private Cabanas", "value": true },
              { "@type": "LocationFeatureSpecification", "name": "Kayaking", "value": true },
              { "@type": "LocationFeatureSpecification", "name": "Paddle Boarding", "value": true },
              { "@type": "LocationFeatureSpecification", "name": "BBQ Grills", "value": true },
              { "@type": "LocationFeatureSpecification", "name": "Glamping", "value": true }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "15",
              "bestRating": "5",
              "worstRating": "5"
            },
            "review": [
              { "@type": "Review", "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "author": { "@type": "Person", "name": "Rachel" }, "reviewBody": "This was so much easier than the river. We'll be back every summer." },
              { "@type": "Review", "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "author": { "@type": "Person", "name": "Kevin" }, "reviewBody": "Wow!! What a wonderful place to take the family!" },
              { "@type": "Review", "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "author": { "@type": "Person", "name": "Carole" }, "reviewBody": "A bit of paradise in an unexpected location!" },
              { "@type": "Review", "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }, "author": { "@type": "Person", "name": "Jim" }, "reviewBody": "Clean, private, no riff-raff. Well worth the money." }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "TouristAttraction",
            "name": "Son's Island",
            "description": "A 3.5-acre private tropical island on Lake Placid near New Braunfels, TX. Lakeside cabana rentals, glamping, kayaking & family-friendly day getaways.",
            "url": "https://sonsisland.com",
            "telephone": "+18775777667",
            "address": { "@type": "PostalAddress", "streetAddress": "110 Lee St", "addressLocality": "Seguin", "addressRegion": "TX", "postalCode": "78155", "addressCountry": "US" },
            "geo": { "@type": "GeoCoordinates", "latitude": 29.5205, "longitude": -97.9036 }
          },
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Son's Island",
            "url": "https://sonsisland.com",
            "telephone": "+18775777667",
            "email": "info@sonsgetaways.com",
            "priceRange": "$$",
            "address": { "@type": "PostalAddress", "streetAddress": "110 Lee St", "addressLocality": "Seguin", "addressRegion": "TX", "postalCode": "78155", "addressCountry": "US" }
          },
          {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "Son's Island — Private Island Daycation Near San Antonio",
            "description": "Tour Son's Island, a private 3.5-acre tropical island near San Antonio with cabana rentals, kayaking, paddle boarding, and family-friendly day trips.",
            "thumbnailUrl": "https://img.youtube.com/vi/HlyMS_ySIgY/maxresdefault.jpg",
            "uploadDate": "2024-05-01",
            "contentUrl": "https://www.youtube.com/watch?v=HlyMS_ySIgY",
            "embedUrl": "https://www.youtube-nocookie.com/embed/HlyMS_ySIgY",
            "publisher": { "@type": "Organization", "name": "Son's Island", "logo": { "@type": "ImageObject", "url": "https://sonsisland.com/og-logo.png" } }
          }
        , faqSchema]}
      />
      <Layout>
        <Hero />
        <IntroBlurb />
        <JumpNav items={jumpNavItems} />
        <VideoSection />
        <PhotoGallery />
        <FeaturesGrid />
        <WatercraftRentalsSection />
        <HowItWorks />
        <CabanasOverviewSection />
        <ActivitiesIconGrid />
        <Testimonials />
        <CabanaPricingCards />
        <PricingPreview />
        <ValueProposition />
        <WhyPrivateMatters />
        <BookCTASection />
        <CallToAction />
        <HoursParkingFAQ faqs={homepageFaqs} />
        <OvernightSisterProperties />
        <OtherProperties />
      </Layout>
    </>
  );
};

export default Index;
